import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { collectionById, productsOf, COLLECTIONS } from "../og/data";
import { useLang } from "../og/i18n";
import { Card, CatFilter } from "../og/Products";
import { Tile } from "../og/Collections";
import { Marquee } from "../og/Marquee";
import { Line } from "../og/Hero";
import { Reveal } from "../og/Reveal";

const Crumbs = ({ items, className = "" }) => (
  <nav className={`f-mono text-[11px] tracking-[0.25em] uppercase flex flex-wrap gap-2 ${className}`}>
    {items.map(([l, to], i) => (
      <span key={l} className="flex gap-2">
        {i > 0 && <span className="opacity-50">/</span>}
        {to ? <Link to={to} className="og-link">{l}</Link> : <span className="opacity-70">{l}</span>}
      </span>
    ))}
  </nav>
);
export { Crumbs };

function CollectionView() {
  const { handle } = useParams();
  const { t, tr } = useLang();
  const [cat, setCat] = useState("all");
  const c = collectionById(handle);
  if (!c) return <Navigate to="/" replace />;

  const all = productsOf(c.id);
  const cats = ["all", ...new Set(all.map((p) => p.cat))];
  const list = cat === "all" ? all : all.filter((p) => p.cat === cat);
  const words = c.title.split(" ");

  return (
    <main data-testid="collection-page" key={c.id}>
      <header className="relative bg-[#FF2A2A] text-black pt-[140px] md:pt-[160px] pb-12 md:pb-16 px-5 md:px-10 overflow-hidden">
        <div className="absolute -right-[6vw] -bottom-[10vw] f-goth text-[50vw] md:text-[36vw] leading-none text-black/10 pointer-events-none select-none">OG</div>
        <Crumbs items={[[t("pp.home"), "/"], [t("nav.collections"), "/#collections"], [c.title]]} className="relative mb-10" />
        <div className="relative f-mono text-xs tracking-[0.3em] mb-4">[ {c.tag} ]</div>
        <h1 data-testid="collection-title" className="relative f-disp uppercase text-[18vw] md:text-[11vw] leading-[0.82]">
          {words.length > 2
            ? [words.slice(0, 2).join(" "), words.slice(2).join(" ")].map((w, i) => <Line key={w} ready delay={0.1 + i * 0.12}>{w}</Line>)
            : <Line ready delay={0.1}>{c.title}</Line>}
        </h1>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }} className="relative mt-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <p className="max-w-md text-base md:text-lg font-medium">{tr(c.desc)}</p>
          <span data-testid="collection-count" className="f-disp text-6xl md:text-7xl leading-none tabular-nums">
            {String(all.length).padStart(2, "0")}<span className="f-mono text-xs tracking-[0.2em] ml-2 align-top">{all.length === 1 ? t("col.piece") : t("col.pieces")}</span>
          </span>
        </motion.div>
      </header>

      <Marquee items={["WELCOME TO OFF GRID", c.title.toUpperCase(), t("top.pre")]} variant="ink" speed={40} />

      <section data-testid="collection-grid" className="bg-[#F5F5F7] text-black px-5 md:px-10 py-16 md:py-24 min-h-[50vh]">
        {all.length > 0 ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
              <CatFilter cat={cat} setCat={setCat} light cats={cats} />
              <span className="f-mono text-xs tracking-[0.2em] text-black/60">{list.length} / {all.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">
              {list.map((p, i) => <Card key={p.id} p={p} i={i} total={list.length} light />)}
            </div>
          </>
        ) : (
          <Reveal className="flex flex-col items-center text-center py-16">
            <img src="/img/logo.gif" alt="Off Grid" className="w-40 h-40" />
            <p data-testid="collection-empty" className="mt-8 f-disp uppercase text-4xl md:text-6xl max-w-3xl leading-[0.9]">{t("col.soon")}</p>
            <Link to="/#nouveautes" data-testid="collection-empty-cta" className="og-btn og-btn--red og-btn--ink mt-10">{t("hero.cta")}</Link>
          </Reveal>
        )}
      </section>

      <section className="bg-[#F5F5F7] text-black px-5 md:px-10 pb-24 md:pb-32 border-t border-black/10 pt-16">
        <h2 className="f-disp uppercase text-5xl md:text-7xl leading-[0.85] mb-10">{t("col.other")}<span className="text-[#FF2A2A]">.</span></h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {COLLECTIONS.filter((x) => x.id !== c.id).map((x) => <Tile key={x.id} c={x} className="h-[360px] md:h-[420px]" />)}
        </div>
      </section>
    </main>
  );
}

export default function CollectionPage() {
  const { handle } = useParams();
  return <CollectionView key={handle} />;
}
